import { updatePackageStatus } from "@/features/packages/services/packagesService";
import { useState } from "react";
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import { Label } from "@shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Textarea } from "@shared/components/ui/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { STATUS_LIST, STATUS_OPTIONS } from "@shared/utils/constants";
import { useTranslation } from "react-i18next";

export default function UpdatePackagesStatus({ packageId, status }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (data) => updatePackageStatus(packageId, data),
  });

  const [newStatus, setNewStatus] = useState(status);
  const [note, setNote] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const toastId = toast.loading(`updated packages...`);
    mutate(
      { status: newStatus, note },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["packages", packageId] });
          toast.success(`Packages updated successfully`, {
            id: toastId,
          });
        },
        onError: (error) => {
          toast.error(`${error.response.data.message}`, { id: toastId });
        },
      }
    );
  };

  return (
    <>
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="outline">
            {t("action.update", {
              entity: t("status.status"),
            })}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("action.update", {
                entity: t("status.status"),
              })}
            </DialogTitle>
            <DialogDescription>
              change {status} to {newStatus}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center space-x-2">
            <div className="grid flex-1 gap-2">
              <Label htmlFor="status">{t("status.status")}</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_LIST.map((element, index) => (
                    <SelectItem key={index} value={element}>
                      {t(STATUS_OPTIONS[element])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Textarea
                id="note"
                placeholder={t("action.add", {
                  entity: t("packages.note"),
                })}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="col-span-3"
              />
            </div>
          </div>

          <DialogFooter className="sm:justify-between">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Close
              </Button>
            </DialogClose>
            <div>
              <Button type="submit" disabled={isPending} onClick={handleSubmit}>
                {isPending ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
